import {characterPose} from './character-motion.js';

const LOOP_SECONDS=8.5; // Nine texture loops fit the 76.5-second scene cycle.
const smooth=x=>{x=Math.max(0,Math.min(1,x));return x*x*(3-2*x);};
export function soundLevels({elapsed,night,paused},hidden=false){
 const active=!paused&&!hidden;
 return {ambience:active?.75+.2*night:0,blow:active?.015*characterPose(elapsed,night).blow:0};
}

// Render small reusable textures once; playback and mixing run in Web Audio.
export function makeTexture(kind,rate=22050){
 const count=Math.round(rate*(LOOP_SECONDS+.2)),data=new Float32Array(count);
 let seed={nature:17,fire:93,crickets:207,blow:311}[kind],low=0,soft=0,pop=0;
 const random=()=>{seed=(Math.imul(seed,1664525)+1013904223)>>>0;return seed/4294967296;};
 for(let i=0;i<count;i++){
  const t=i/rate,n=random()*2-1;low+=.018*(n-low);soft+=.18*(n-soft);
  if(kind==='nature')data[i]=low*1.8*(.7+.18*Math.sin(2*Math.PI*t/LOOP_SECONDS)) + soft*.025;
  if(kind==='blow')data[i]=(soft-low)*.9;
  if(kind==='fire'){
   if(random()<4/rate)pop=.2+random()*.7;
   pop*=Math.exp(-1/(rate*.009));
   data[i]=low*.9+(n-soft)*pop*.6+soft*.065;
  }
  if(kind==='crickets'){
   const phrase=(t%1.7)/1.7;
   const gate=smooth(phrase/.08)*(1-smooth((phrase-.48)/.15));
   const chirp=Math.pow(Math.max(0,Math.sin(2*Math.PI*30*t)),4);
   data[i]=gate*chirp*(Math.sin(2*Math.PI*4200*t)+.24*Math.sin(2*Math.PI*5700*t))*.35;
  }
 }
 // Wrap the tail into the opening to eliminate a discontinuity at the seam.
 const fade=Math.round(rate*.2);
 for(let i=0;i<fade;i++){
  const w=smooth(i/(fade-1));
  data[count-fade+i]=data[count-fade+i]*(1-w)+data[i]*w;
 }
 return data.subarray(fade);
}

export function createCampAudio(onChange=()=>{}){
 let context,master,voices=[],enabled=false,volume=.45,state={elapsed:0,night:0,paused:true};
 let anchorElapsed=0,anchorAudio=0,wasPlaying=false,suspendTimer;
 const textures=new Map();
 const report=()=>onChange({enabled,volume});
 function gain(param,value,time=.04){param.cancelScheduledValues(context.currentTime);param.setTargetAtTime(value,context.currentTime,time);}
 function stopVoices(){for(const voice of voices){gain(voice.gain.gain,0,.015);voice.source.stop(context.currentTime+.12);}voices=[];}
 function startVoices(){
  stopVoices();anchorElapsed=state.elapsed;anchorAudio=context.currentTime;
  for(const kind of ['ambience','blow']){
   const source=context.createBufferSource(),node=context.createGain();source.buffer=textures.get(kind);source.loop=true;node.gain.value=0;
   source.connect(node).connect(master);
   source.onended=()=>{source.disconnect();node.disconnect();};
   source.start(0,((state.elapsed/1000)%source.buffer.duration+source.buffer.duration)%source.buffer.duration);
   voices.push({source,gain:node,kind});
  }
 }
 function sync(next=state){
  state=next;if(!context)return;
  const playing=enabled&&!state.paused&&!document.hidden;
  if(!playing){
   if(wasPlaying||voices.length){gain(master.gain,0,.025);stopVoices();}
   wasPlaying=false;
   if(!suspendTimer)suspendTimer=setTimeout(()=>{suspendTimer=null;if(!wasPlaying)void context.suspend();},180);
   return;
  }
  clearTimeout(suspendTimer);suspendTimer=null;
  if(context.state==='suspended')void context.resume().catch(()=>{enabled=false;report();});
  const drift=Math.abs((state.elapsed-anchorElapsed)/1000-(context.currentTime-anchorAudio));
  if(!wasPlaying||drift>.15)startVoices();
  wasPlaying=true;gain(master.gain,volume,.05);
  const levels=soundLevels(state);
  for(const voice of voices)gain(voice.gain.gain,levels[voice.kind],voice.kind==='blow'?.018:.12);
 }
 async function toggle(){
  if(!context){
   const Audio=window.AudioContext||window.webkitAudioContext;
   if(!Audio)throw new Error('Audio is unavailable in this browser.');
   context=new Audio();master=context.createGain();master.gain.value=0;master.connect(context.destination);
   for(const kind of ['blow']){
    const samples=makeTexture(kind),buffer=context.createBuffer(1,samples.length,22050);buffer.copyToChannel(samples,0);textures.set(kind,buffer);
   }
  }
  if(!textures.has('ambience')){
   await context.resume();
   const response=await fetch('./public/audio/camp-loop.wav');
   if(!response.ok)throw new Error('Unable to load campsite recording.');
   textures.set('ambience',await context.decodeAudioData(await response.arrayBuffer()));
  }
  enabled=!enabled;
  if(enabled)await context.resume();
  sync();report();return enabled;
 }
 document.addEventListener('visibilitychange',()=>sync());
 return {sync,toggle,setVolume(value){volume=Math.max(0,Math.min(1,value));sync();report();},
  snapshot(){return {enabled,volume,contextState:context?.state||'not-created',voices:voices.length,levels:soundLevels(state,document.hidden),elapsed:state.elapsed};}};
}
